'use client'

import type { ComponentProps, Dispatch, Ref, SetStateAction } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  X,
  User,
  Users,
  PlusCircle,
  ChevronLeft,
  ChevronDown,
  Loader2,
  ListPlus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { CommunityInviteFilterPanel } from '@/components/community-invite-filter-panel'
import { CommunityInviteResultsTable } from '@/components/community-invite-results-table'
import { ShareRoomCommunityStepAudience } from '@/components/share-room/share-room-community-step-audience'
import { ShareRoomCommunityStepReviewSection } from '@/components/share-room/share-room-community-step-review-section'
import { ShareRoomInviteMessageFooter } from '@/components/share-room/share-room-invite-message-footer'
import type {
  ContactOrUser,
  InviteFetchOptions,
  ShareRoomGuestInviteRow,
} from '@/components/share-room/types'
import { SHARE_ROOM_CONTACT_TAKE } from '@/lib/share-room/directory'

export type CommunityInviteFilterPanelSpreadProps = ComponentProps<
  typeof CommunityInviteFilterPanel
>

export type ShareRoomInternalInviteColumnProps = {
  showDirectoryLoadingStrip: boolean
  showCommunityInviteWizard: boolean
  communityWizardStep: number
  setCommunityWizardStep: (step: number) => void
  audienceMethod: 'filter' | 'quick'
  setAudienceMethod: Dispatch<SetStateAction<'filter' | 'quick'>>
  isCommunityWizardStep2: boolean
  isCommunityFilterStep2: boolean
  communityFilterPanelProps: CommunityInviteFilterPanelSpreadProps | null
  roomId: string
  accountName?: string | null
  workspaceName?: string | null
  isCommunityRoom: boolean
  isInternalInRoomTenant: boolean
  selectedContactsAndUsers: ContactOrUser[]
  setSelectedContactsAndUsers: Dispatch<SetStateAction<ContactOrUser[]>>
  searchTerm: string
  setSearchTerm: Dispatch<SetStateAction<string>>
  setShowSuggestions: Dispatch<SetStateAction<boolean>>
  searchTimeout: NodeJS.Timeout | null
  setSearchTimeout: Dispatch<SetStateAction<NodeJS.Timeout | null>>
  fetchContactsAndUsers: (searchTerm?: string, options?: InviteFetchOptions) => void | Promise<void>
  communityInviteFilterOptions: InviteFetchOptions
  inputRef: Ref<HTMLInputElement>
  suggestionsRef: Ref<HTMLDivElement>
  showSuggestions: boolean
  contactsAndUsers: ContactOrUser[]
  communityFilterLoading: boolean
  communityFilterResultsReady: boolean
  communityInviteTableRows: ContactOrUser[]
  selectedIdSet: Set<string>
  handleCommunityRowToggle: (item: ContactOrUser, checked: boolean) => void
  handleToggleSelectAllInvitePicker: () => void
  invitePickerItems: ContactOrUser[]
  handleSelectContactOrUser: (item: ContactOrUser) => void
  handleRemoveContactOrUser: (id: string) => void
  contentName?: string
  onSelectAllRoomMembers?: () => void
  isSelectingAllRoomMembers?: boolean
  onAddAllContacts?: () => void
  addAllContactsCount?: number
  directoryTruncated?: boolean
  directoryLimit?: number
  guestInviteRows: ShareRoomGuestInviteRow[]
  setGuestInviteRows: Dispatch<SetStateAction<ShareRoomGuestInviteRow[]>>
  setAddContactInitialEmail: Dispatch<SetStateAction<string>>
  setIsAddContactOpen: Dispatch<SetStateAction<boolean>>
  setIsAddUserOpen: Dispatch<SetStateAction<boolean>>
  showCommunityBulkCrmSection: boolean
  communityBulkNoAccount: boolean
  setCommunityBulkNoAccount: Dispatch<SetStateAction<boolean>>
  communityBulkAccountId: string | null
  setCommunityBulkAccountId: Dispatch<SetStateAction<string | null>>
  communityBulkAccountName: string | null
  setCommunityBulkAccountName: Dispatch<SetStateAction<string | null>>
  communityBulkAccountSearch: string
  setCommunityBulkAccountSearch: Dispatch<SetStateAction<string>>
  communityBulkAccountHits: { id: string; name: string }[]
  setCommunityBulkAccountHits: Dispatch<SetStateAction<{ id: string; name: string }[]>>
  communityBulkAccountSearchLoading: boolean
  bulkInvitePasteOpen: boolean
  setBulkInvitePasteOpen: Dispatch<SetStateAction<boolean>>
  bulkInvitePasteText: string
  setBulkInvitePasteText: Dispatch<SetStateAction<string>>
  applyBulkInvitePaste: () => void
  showCommunityWizardStep2Nav: boolean
  showMessageFooter: boolean
  messageFooterProps: ComponentProps<typeof ShareRoomInviteMessageFooter>
}

export function ShareRoomInternalInviteColumn({
  showDirectoryLoadingStrip,
  showCommunityInviteWizard,
  communityWizardStep,
  setCommunityWizardStep,
  audienceMethod,
  setAudienceMethod,
  isCommunityWizardStep2,
  isCommunityFilterStep2,
  communityFilterPanelProps,
  roomId,
  accountName,
  workspaceName,
  isCommunityRoom,
  isInternalInRoomTenant,
  selectedContactsAndUsers,
  setSelectedContactsAndUsers,
  searchTerm,
  setSearchTerm,
  setShowSuggestions,
  searchTimeout,
  setSearchTimeout,
  fetchContactsAndUsers,
  communityInviteFilterOptions,
  inputRef,
  suggestionsRef,
  showSuggestions,
  contactsAndUsers,
  communityFilterLoading,
  communityFilterResultsReady,
  communityInviteTableRows,
  selectedIdSet,
  handleCommunityRowToggle,
  handleToggleSelectAllInvitePicker,
  invitePickerItems,
  handleSelectContactOrUser,
  handleRemoveContactOrUser,
  contentName,
  onSelectAllRoomMembers,
  isSelectingAllRoomMembers = false,
  onAddAllContacts,
  addAllContactsCount = 0,
  directoryTruncated = false,
  directoryLimit = 0,
  guestInviteRows,
  setGuestInviteRows,
  setAddContactInitialEmail,
  setIsAddContactOpen,
  setIsAddUserOpen,
  showCommunityBulkCrmSection,
  communityBulkNoAccount,
  setCommunityBulkNoAccount,
  communityBulkAccountId,
  setCommunityBulkAccountId,
  communityBulkAccountName,
  setCommunityBulkAccountName,
  communityBulkAccountSearch,
  setCommunityBulkAccountSearch,
  communityBulkAccountHits,
  setCommunityBulkAccountHits,
  communityBulkAccountSearchLoading,
  bulkInvitePasteOpen,
  setBulkInvitePasteOpen,
  bulkInvitePasteText,
  setBulkInvitePasteText,
  applyBulkInvitePaste,
  showCommunityWizardStep2Nav,
  showMessageFooter,
  messageFooterProps,
}: ShareRoomInternalInviteColumnProps) {
  const showContentShareSelectAll =
    Boolean(contentName) && !isCommunityWizardStep2 && Boolean(onSelectAllRoomMembers)
  const showRoomShareAddAll =
    !contentName &&
    !isCommunityRoom &&
    isInternalInRoomTenant &&
    !isCommunityWizardStep2 &&
    searchTerm.trim().length === 0 &&
    addAllContactsCount > 0 &&
    Boolean(onAddAllContacts)
  const showPickerHelperRow =
    !isCommunityWizardStep2 &&
    !(showCommunityInviteWizard && communityWizardStep === 3) &&
    (selectedContactsAndUsers.length > 0 || showContentShareSelectAll || showRoomShareAddAll)
  const truncatedHint =
    directoryTruncated &&
    (isCommunityWizardStep2 || showSuggestions || isCommunityFilterStep2)
      ? searchTerm.trim().length > 0
        ? `Showing the first ${directoryLimit || SHARE_ROOM_CONTACT_TAKE.search} matches. Try a more specific name or email.`
        : `Showing the first ${directoryLimit || SHARE_ROOM_CONTACT_TAKE.unfiltered} contacts. Type a name or email to find someone else.`
      : null

  return (
    <div className="min-w-0 flex-[0.95] space-y-4 lg:max-w-xl">
      {showDirectoryLoadingStrip && (
        <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" aria-hidden />
          <span>Loading people directory…</span>
        </div>
      )}
      {showCommunityInviteWizard && communityWizardStep === 1 ? (
        <ShareRoomCommunityStepAudience
          audienceMethod={audienceMethod}
          onAudienceMethodChange={setAudienceMethod}
          onContinue={() => setCommunityWizardStep(2)}
        />
      ) : (
        <div className="space-y-4">
          <div
            className={cn(
              'space-y-4',
              isCommunityWizardStep2 &&
                audienceMethod === 'filter' &&
                'grid min-w-0 gap-4 lg:grid-cols-[minmax(260px,320px)_1fr] lg:items-start'
            )}
          >
            {isCommunityWizardStep2 && audienceMethod === 'filter' && communityFilterPanelProps && (
              <CommunityInviteFilterPanel {...communityFilterPanelProps} />
            )}
            <div
              className={cn(
                'flex min-w-0 flex-col gap-3',
                isCommunityWizardStep2 && 'min-h-[min(440px,58vh)]'
              )}
            >
              {isCommunityWizardStep2 && (
                <p className="text-xs font-medium text-slate-600">
                  {audienceMethod === 'filter'
                    ? 'Use filters on the left, then select people in the table. Search narrows the list.'
                    : 'Search by name or email, then use the checkboxes to select people.'}
                </p>
              )}
              <div className="relative">
                {isCommunityWizardStep2 ? (
                  <div className="space-y-2">
                    {selectedContactsAndUsers.length > 0 && (
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 text-xs text-slate-700">
                        <span>
                          <strong className="text-slate-900">{selectedContactsAndUsers.length}</strong>{' '}
                          selected — use the table to add or remove people
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 shrink-0 px-2 text-xs text-slate-700"
                          onClick={() => setSelectedContactsAndUsers([])}
                        >
                          Clear all
                        </Button>
                      </div>
                    )}
                    <form
                      className="flex min-h-[40px] flex-1 items-center rounded-md border border-slate-200 p-1"
                      autoComplete="off"
                      onSubmit={(e) => e.preventDefault()}
                    >
                      <Input
                        id={`share-room-search-${roomId}`}
                        name={`people-search-${roomId}`}
                        ref={inputRef}
                        type="search"
                        enterKeyHint="search"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-form-type="other"
                        aria-autocomplete="list"
                        role="combobox"
                        placeholder={
                          selectedContactsAndUsers.length
                            ? 'Narrow list by name or email (optional)'
                            : audienceMethod === 'filter'
                              ? 'Narrow by name or email (optional)'
                              : isInternalInRoomTenant
                                ? isCommunityRoom
                                  ? 'Search contacts from any account or add team members'
                                  : 'Search contacts or team members'
                                : 'Search contacts or add someone by email'
                        }
                        value={searchTerm}
                        onChange={(e) => {
                          const value = e.target.value
                          setSearchTerm(value)
                          setShowSuggestions(true)

                          if (searchTimeout) {
                            clearTimeout(searchTimeout)
                          }

                          const filterOpts =
                            showCommunityInviteWizard &&
                            communityWizardStep === 2 &&
                            audienceMethod === 'filter'
                              ? communityInviteFilterOptions
                              : undefined

                          if (value.trim().length > 0) {
                            const timeoutId = setTimeout(() => {
                              void fetchContactsAndUsers(value.trim(), filterOpts)
                            }, 300)
                            setSearchTimeout(timeoutId)
                          } else {
                            void fetchContactsAndUsers(undefined, filterOpts)
                          }
                        }}
                        onFocus={() => setShowSuggestions(true)}
                        className="flex-grow border-none p-0 px-2 text-sm focus:ring-0"
                      />
                    </form>
                  </div>
                ) : showCommunityInviteWizard && communityWizardStep === 3 ? (
                  <ShareRoomCommunityStepReviewSection
                    selectedContactsAndUsers={selectedContactsAndUsers}
                    onRemoveSelected={handleRemoveContactOrUser}
                    guestInviteRows={guestInviteRows}
                    onRemoveGuestEmail={(email) =>
                      setGuestInviteRows((prev) => prev.filter((x) => x.email !== email))
                    }
                  />
                ) : (
                  <form
                    className={cn(
                      'flex min-h-[40px] flex-wrap items-center gap-1 rounded-md border p-1',
                      contentName &&
                        selectedContactsAndUsers.length > 0 &&
                        'max-h-36 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]'
                    )}
                    autoComplete="off"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    {selectedContactsAndUsers.map((item) => (
                      <div
                        key={item.id}
                        className={`flex items-center rounded-full px-2 py-0.5 text-xs ${
                          item.type === 'user'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-green-500 text-white'
                        }`}
                      >
                        <Avatar className="mr-1 h-4 w-4">
                          <AvatarImage
                            src={
                              item.image ||
                              `https://api.dicebear.com/6.x/initials/svg?seed=${item.firstName} ${item.lastName}`
                            }
                            alt={`${item.firstName} ${item.lastName}`}
                          />
                          <AvatarFallback className="text-[10px]">
                            {item.firstName[0]}
                            {item.lastName[0]}
                          </AvatarFallback>
                        </Avatar>
                        <span>
                          {item.firstName} {item.lastName}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveContactOrUser(item.id)}
                          className="ml-1 rounded-full hover:bg-green-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    {(!showCommunityInviteWizard || communityWizardStep === 2) && (
                      <Input
                        id={`share-room-search-${roomId}`}
                        name={`people-search-${roomId}`}
                        ref={inputRef}
                        type="search"
                        enterKeyHint="search"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-form-type="other"
                        aria-autocomplete="list"
                        role="combobox"
                        placeholder={
                          selectedContactsAndUsers.length
                            ? 'Add more people'
                            : showCommunityInviteWizard &&
                                communityWizardStep === 2 &&
                                audienceMethod === 'filter'
                              ? 'Narrow by name or email (optional)'
                              : isInternalInRoomTenant
                                ? isCommunityRoom
                                  ? 'Search contacts from any account or add team members'
                                  : 'Search contacts or team members'
                                : 'Search contacts or add someone by email'
                        }
                        value={searchTerm}
                        onChange={(e) => {
                          const value = e.target.value
                          setSearchTerm(value)
                          setShowSuggestions(true)

                          if (searchTimeout) {
                            clearTimeout(searchTimeout)
                          }

                          const filterOpts =
                            showCommunityInviteWizard &&
                            communityWizardStep === 2 &&
                            audienceMethod === 'filter'
                              ? communityInviteFilterOptions
                              : undefined

                          if (value.trim().length > 0) {
                            const timeoutId = setTimeout(() => {
                              void fetchContactsAndUsers(value.trim(), filterOpts)
                            }, 300)
                            setSearchTimeout(timeoutId)
                          } else {
                            void fetchContactsAndUsers(undefined, filterOpts)
                          }
                        }}
                        onFocus={() => setShowSuggestions(true)}
                        className="flex-grow border-none p-0 text-sm focus:ring-0"
                      />
                    )}
                  </form>
                )}
                {showPickerHelperRow && (
                  <div className="mt-1 text-xs text-slate-500">
                    {selectedContactsAndUsers.length > 0 && (
                      <span>
                        {selectedContactsAndUsers.length}{' '}
                        {selectedContactsAndUsers.length === 1 ? 'person' : 'people'} selected
                        {(showContentShareSelectAll || showRoomShareAddAll) && (
                          <span className="mx-1.5 text-slate-300" aria-hidden>
                            ·
                          </span>
                        )}
                      </span>
                    )}
                    {showContentShareSelectAll && (
                      <button
                        type="button"
                        className="text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={onSelectAllRoomMembers}
                        disabled={isSelectingAllRoomMembers}
                      >
                        {isSelectingAllRoomMembers ? 'Selecting…' : 'Select all room members'}
                      </button>
                    )}
                    {showRoomShareAddAll && (
                      <button
                        type="button"
                        className="text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline"
                        onClick={onAddAllContacts}
                      >
                        Add all {addAllContactsCount}{' '}
                        {addAllContactsCount === 1 ? 'contact' : 'contacts'}
                      </button>
                    )}
                  </div>
                )}
                {(!showCommunityInviteWizard || communityWizardStep === 2) &&
                  (isCommunityWizardStep2 || showSuggestions || isCommunityFilterStep2) && (
                    <div
                      ref={suggestionsRef}
                      className={cn(
                        'z-10 w-full overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]',
                        isCommunityWizardStep2
                          ? 'relative mt-2 flex max-h-[min(56vh,28rem)] min-w-0 flex-col gap-2 rounded-md border border-slate-200 bg-white shadow-sm'
                          : 'absolute mt-1 max-h-60 w-full rounded-md border border-slate-200 bg-white shadow-lg'
                      )}
                    >
                      {isCommunityWizardStep2 && communityFilterLoading && (
                        <div
                          className="absolute inset-0 z-30 flex items-center justify-center rounded-md bg-white/75"
                          aria-busy="true"
                          aria-live="polite"
                        >
                          <Loader2 className="h-8 w-8 animate-spin text-slate-600" aria-hidden />
                        </div>
                      )}
                      {isCommunityWizardStep2 ? (
                        <>
                          {isCommunityFilterStep2 &&
                            !communityFilterResultsReady &&
                            !communityFilterLoading && (
                              <div className="border-b border-slate-100 bg-slate-50/80 p-3 text-sm text-slate-600">
                                Click <span className="font-medium text-slate-800">Apply filters</span> on the left to
                                load people who match your criteria.
                              </div>
                            )}
                          {isCommunityFilterStep2 &&
                            communityFilterResultsReady &&
                            contactsAndUsers.length === 0 &&
                            selectedContactsAndUsers.length > 0 &&
                            !communityFilterLoading && (
                              <div className="border-b border-amber-100 bg-amber-50/90 p-2 text-xs text-slate-600">
                                Current filters didn&apos;t match anyone new. People you already selected stay in the
                                table so you can uncheck them.
                              </div>
                            )}
                          <div className="min-h-0 min-w-0 flex-1 overflow-auto">
                            <CommunityInviteResultsTable
                              items={communityInviteTableRows}
                              selectedIds={selectedIdSet}
                              onToggleRow={handleCommunityRowToggle}
                              onToggleSelectAllVisible={handleToggleSelectAllInvitePicker}
                              accountName={accountName ?? undefined}
                              workspaceName={workspaceName ?? undefined}
                              className="rounded-none border-0 shadow-none"
                            />
                          </div>
                          {truncatedHint && (
                            <div className="sticky bottom-0 border-t border-slate-100 bg-white px-3 py-2 text-xs text-slate-600">
                              {truncatedHint}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {invitePickerItems.map((item) => (
                            <div
                              key={item.id}
                              className="flex min-w-0 cursor-pointer items-center gap-2 p-1.5 text-sm hover:bg-gray-100"
                              onMouseDown={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleSelectContactOrUser(item)
                              }}
                            >
                              <Avatar className="h-6 w-6 shrink-0">
                                <AvatarImage
                                  src={
                                    item.image ||
                                    `https://api.dicebear.com/6.x/initials/svg?seed=${item.firstName} ${item.lastName}`
                                  }
                                  alt={`${item.firstName} ${item.lastName}`}
                                />
                                <AvatarFallback className="text-xs">
                                  {item.firstName[0]}
                                  {item.lastName[0]}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-medium">
                                  {item.firstName} {item.lastName}
                                </div>
                                <div className="truncate text-xs text-gray-500">
                                  {item.title && <span>{item.title} • </span>}
                                  {item.email}
                                </div>
                              </div>
                              <Badge
                                variant={item.type === 'user' ? 'outline' : 'secondary'}
                                className={`shrink-0 whitespace-nowrap ${
                                  item.type === 'user'
                                    ? 'bg-gray-100 text-gray-800'
                                    : 'bg-green-500 text-white'
                                }`}
                              >
                                {item.type === 'user' ? (
                                  <>
                                    <Users className="mr-1 h-3 w-3 shrink-0" />{' '}
                                    {workspaceName?.trim() || 'Workspace'}
                                  </>
                                ) : (
                                  <>
                                    <User className="mr-1 h-3 w-3 shrink-0" />{' '}
                                    {item.accountName || accountName || 'Contact'}
                                  </>
                                )}
                              </Badge>
                            </div>
                          ))}
                          {truncatedHint && (
                            <div className="sticky bottom-0 border-t border-slate-100 bg-white px-3 py-2 text-xs text-slate-600">
                              {truncatedHint}
                            </div>
                          )}
                        </>
                      )}
                      {communityFilterResultsReady &&
                        isCommunityFilterStep2 &&
                        searchTerm.trim().length === 0 &&
                        contactsAndUsers.length === 0 &&
                        selectedContactsAndUsers.length === 0 &&
                        !communityFilterLoading && (
                          <div className="p-3 text-sm text-slate-600">
                            No one matches these filters yet. Try different specific accounts or account types, clearing
                            the title keyword, or turning on &quot;Include contacts with no account&quot; or internal
                            teammates.
                          </div>
                        )}
                      {searchTerm.trim().length > 0 &&
                        (isCommunityWizardStep2
                          ? communityInviteTableRows.length === 0
                          : invitePickerItems.length === 0) &&
                        (!isCommunityFilterStep2 || communityFilterResultsReady) && (
                          <div
                            className="p-2 text-sm text-gray-500"
                            onMouseDown={(e) => {
                              e.stopPropagation()
                            }}
                          >
                            <div>
                              {isCommunityRoom
                                ? `No contacts or team members found matching "${searchTerm}"`
                                : isInternalInRoomTenant
                                  ? 'No contacts or team members found'
                                  : `No contacts found for "${searchTerm}"`}
                            </div>
                            <div className="mt-2 flex flex-col space-y-2">
                              <button
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  setAddContactInitialEmail(searchTerm.trim())
                                  setIsAddContactOpen(true)
                                  setShowSuggestions(false)
                                }}
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                }}
                                className="flex w-full cursor-pointer items-center text-left text-blue-600 hover:text-blue-800"
                              >
                                <PlusCircle className="mr-1 h-4 w-4" />
                                <User className="mr-1 h-4 w-4" />
                                {isInternalInRoomTenant ? 'Create new contact' : 'Add them by email'}
                              </button>
                              {isInternalInRoomTenant && (
                                <button
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setIsAddUserOpen(true)
                                    setShowSuggestions(false)
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                  }}
                                  className="flex w-full cursor-pointer items-center text-left text-blue-600 hover:text-blue-800"
                                >
                                  <PlusCircle className="mr-1 h-4 w-4" />
                                  <Users className="mr-1 h-4 w-4" />
                                  Create new team member
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      {!isInternalInRoomTenant &&
                        !(searchTerm.trim().length > 0 && invitePickerItems.length === 0) && (
                          <div
                            className="border-t border-gray-100 bg-gray-50/80 p-2"
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setAddContactInitialEmail(searchTerm.trim())
                                setIsAddContactOpen(true)
                                setShowSuggestions(false)
                              }}
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                              }}
                              className="flex w-full cursor-pointer items-center text-left text-sm font-medium text-blue-600 hover:text-blue-800"
                            >
                              <PlusCircle className="mr-2 h-4 w-4 shrink-0" />
                              Can&apos;t find them? Add someone by email
                            </button>
                          </div>
                        )}
                    </div>
                  )}
              </div>
            </div>
          </div>

          {guestInviteRows.length > 0 && (
            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-2">
              <p className="mb-2 text-xs font-medium text-slate-700">Queued email invites</p>
              <div className="flex max-h-24 flex-wrap gap-2 overflow-y-auto">
                {guestInviteRows.map((r) => (
                  <div
                    key={r.email}
                    className="inline-flex max-w-full items-center gap-1 rounded-full bg-white px-2 py-1 text-xs text-slate-800 shadow-sm"
                  >
                    <span className="truncate">{r.email}</span>
                    <button
                      type="button"
                      className="rounded-full p-0.5 text-slate-500 hover:bg-slate-100"
                      aria-label={`Remove ${r.email}`}
                      onClick={() =>
                        setGuestInviteRows((prev) => prev.filter((x) => x.email !== r.email))
                      }
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showCommunityBulkCrmSection && (
            <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <p className="text-sm font-medium text-slate-900">Contact CRM context</p>
              <p className="text-xs text-slate-600">
                Link pasted invites to an account, or create contacts without an account.
              </p>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="bulk-no-account"
                  checked={communityBulkNoAccount}
                  onCheckedChange={(c) => {
                    const checked = c === true
                    setCommunityBulkNoAccount(checked)
                    if (checked) {
                      setCommunityBulkAccountId(null)
                      setCommunityBulkAccountName(null)
                    }
                  }}
                />
                <Label htmlFor="bulk-no-account" className="cursor-pointer text-sm font-normal">
                  Contacts without account
                </Label>
              </div>
              {!communityBulkNoAccount && (
                <div className="space-y-1">
                  {communityBulkAccountId ? (
                    <div className="flex items-center justify-between rounded-md border bg-white px-2 py-1.5 text-sm">
                      <span className="truncate">{communityBulkAccountName}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 shrink-0"
                        onClick={() => {
                          setCommunityBulkAccountId(null)
                          setCommunityBulkAccountName(null)
                        }}
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Input
                        placeholder="Search for an account…"
                        value={communityBulkAccountSearch}
                        onChange={(e) => setCommunityBulkAccountSearch(e.target.value)}
                      />
                      {communityBulkAccountSearchLoading && (
                        <p className="text-xs text-slate-500">Searching…</p>
                      )}
                      <ul className="max-h-32 overflow-y-auto rounded-md border border-slate-200 bg-white text-sm">
                        {communityBulkAccountHits.map((a) => (
                          <li key={a.id}>
                            <button
                              type="button"
                              className="w-full px-3 py-2 text-left hover:bg-slate-50"
                              onClick={() => {
                                setCommunityBulkAccountId(a.id)
                                setCommunityBulkAccountName(a.name)
                                setCommunityBulkAccountSearch('')
                                setCommunityBulkAccountHits([])
                              }}
                            >
                              {a.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="border-t border-dashed border-slate-200/90 pt-2 !mt-2">
            <Collapsible open={bulkInvitePasteOpen} onOpenChange={setBulkInvitePasteOpen}>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="inline-flex max-w-full items-center gap-2 rounded-md py-1.5 pl-0 pr-2 text-left text-[11px] leading-snug text-slate-500 outline-none ring-offset-background transition-colors hover:bg-slate-50/80 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                >
                  <ListPlus className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
                  <span className="min-w-0">
                    <span className="text-slate-400">Optional — </span>
                    <span className="text-slate-600">paste a list to add several people</span>
                  </span>
                  <ChevronDown
                    className={cn(
                      'ml-0.5 h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform',
                      bulkInvitePasteOpen && 'rotate-180'
                    )}
                    aria-hidden
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-2 border-l-2 border-slate-200 pl-3 pt-2">
                <Textarea
                  placeholder="First last <name@company.com>; Jane Doe <jane@company.com>"
                  value={bulkInvitePasteText}
                  onChange={(e) => setBulkInvitePasteText(e.target.value)}
                  rows={4}
                  className="resize-y font-mono text-xs"
                />
                <Button type="button" size="sm" variant="secondary" onClick={applyBulkInvitePaste}>
                  Add from pasted list
                </Button>
                <p className="text-[11px] text-slate-500">
                  Separate entries with semicolons, commas, or new lines. Example: Name &lt;email&gt;. Use semicolons
                  between entries if a name contains a comma.
                </p>
              </CollapsibleContent>
            </Collapsible>
          </div>

          {showCommunityWizardStep2Nav && (
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setCommunityWizardStep(1)}>
                <ChevronLeft className="mr-1 h-4 w-4" aria-hidden />
                Back
              </Button>
              <Button
                type="button"
                disabled={selectedContactsAndUsers.length === 0 && guestInviteRows.length === 0}
                onClick={() => setCommunityWizardStep(3)}
              >
                Continue to review
              </Button>
            </div>
          )}

          {showMessageFooter && <ShareRoomInviteMessageFooter {...messageFooterProps} />}
        </div>
      )}
    </div>
  )
}
